import React, { useEffect, useMemo, useState } from 'react';

import downloadLargeTokens from '@amzn/meridian-tokens/base/icon/download-large';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import ButtonGroup, { ButtonOption } from '@amzn/meridian/button-group';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';

import { GridApi } from 'ag-grid-community';
import { useReportingData } from 'src/hooks';
import { useSearchParam } from 'src/hooks/useSearchParam/useSearchParam';
import { getCurrentDate } from 'src/util/dateTimeUtil';

import { BaseAgGrid } from '../BaseAgGrid/BaseAgGrid';
import { FilterInput } from '../FilterInput';
import './Reporting.scss';
import { reportingGridColDefs } from './reportingGridColDefs';

export const Reporting: React.FC = () => {
    // URL search params for sharing filters
    const [filterTextParam, setFilterTextParam] = useSearchParam('filter');
    const [serviceFilterParam, setServiceFilterParam] = useSearchParam('service', 'all');
    const [gridFilterParam, setGridFilterParam] = useSearchParam('gridFilter');

    const [filterText, setFilterText] = useState(filterTextParam || '');
    const [agGridApi, setAgGridApi] = useState<GridApi>();
    const [serviceFilterValue, setServiceFilterValue] = useState(serviceFilterParam || 'all');
    const { isLoading, combinedData, loadingProgress } = useReportingData();

    // Memoize the grid data to prevent unnecessary re-renders
    const gridData = useMemo(() => combinedData, [combinedData]);

    // Update URL when filter text changes
    useEffect(() => {
        setFilterTextParam(filterText || undefined);
    }, [filterText]);

    // Update URL when service filter changes
    useEffect(() => {
        setServiceFilterParam(serviceFilterValue);
    }, [serviceFilterValue]);

    // Export grid data to CSV
    const exportToCSV = () => {
        const params = {
            fileName: `reporting-export-${getCurrentDate()}.csv`
        };
        agGridApi?.exportDataAsCsv(params);
    };

    // Apply service filter when it changes
    useEffect(() => {
        // Clear existing filter
        const currentFilterModel = agGridApi?.getFilterModel();
        const newFilterModel = { ...currentFilterModel };

        // Remove service filter if it exists
        if (newFilterModel.serviceName) {
            delete newFilterModel.serviceName;
        }

        // Apply new filter based on selection
        if (serviceFilterValue !== 'all') {
            newFilterModel.serviceName = {
                filterType: 'set',
                values: [serviceFilterValue === 'prime' ? 'Prime' : 'Free']
            };
        }

        // Apply the updated filter model
        agGridApi?.setFilterModel(newFilterModel);
    }, [serviceFilterValue, agGridApi]);

    // Apply text filter from URL after grid is ready and data is loaded
    useEffect(() => {
        if (agGridApi && filterTextParam && !isLoading && gridData && gridData.length > 0) {
            // Add a delay to ensure grid is fully rendered and data is loaded
            const timer = setTimeout(() => {
                setFilterText(filterTextParam);
                agGridApi.setGridOption('quickFilterText', filterTextParam);
            }, 0);

            return () => clearTimeout(timer);
        }
    }, [agGridApi, filterTextParam, isLoading, gridData]);

    // Apply grid filters from URL when grid is ready
    useEffect(() => {
        if (agGridApi && gridFilterParam) {
            try {
                const filterModel = JSON.parse(decodeURIComponent(gridFilterParam));
                agGridApi.setFilterModel(filterModel);
            } catch (error) {
                console.error('Failed to parse grid filter from URL:', error);
            }
        }
    }, [agGridApi, gridFilterParam]);

    // Save grid filters to URL when they change
    const onFilterChanged = () => {
        if (agGridApi) {
            const filterModel = agGridApi.getFilterModel();
            if (Object.keys(filterModel).length > 0) {
                setGridFilterParam(encodeURIComponent(JSON.stringify(filterModel)));
            } else {
                setGridFilterParam(undefined);
            }
        }
    };

    const gridOptions = useMemo(
        () => ({
            enableCellTextSelection: true,
            suppressRowClickSelection: true,
            rowBuffer: 20, // Increase row buffer for smoother scrolling
            cacheBlockSize: 100, // Adjust cache block size for better performance
            maxBlocksInCache: 10, // Limit blocks in cache to prevent memory issues
            pagination: true, // Enable pagination to reduce rendering load
            paginationPageSize: 100 // Show 100 rows per page
        }),
        []
    );

    const defaultColDef = useMemo(() => {
        return {
            // allow every column to be aggregated
            enableValue: true,
            // allow every column to be grouped
            enableRowGroup: true,
            // allow every column to be pivoted
            enablePivot: true
        };
    }, []);

    // auto-size columns after grid and data are ready
    useEffect(() => {
        if (agGridApi && !isLoading && gridData && gridData.length > 0) {
            // Add a delay to ensure grid is fully rendered
            setTimeout(() => {
                try {
                    agGridApi.autoSizeAllColumns();
                } catch (error) {
                    console.warn('Could not auto-size columns:', error);
                }
            }, 0);
        }
    }, [agGridApi, isLoading, gridData]);

    return (
        <Column className="reporting-grid" width="100%" height="100%" minHeight={480} spacing="300">
            <Box type="fill" spacingInset="400">
                <Row alignmentHorizontal="justify" spacing="300">
                    <FilterInput
                        filterText={filterText}
                        setFilterText={(value) => {
                            setFilterText(value);
                        }}
                    />
                    <Row>
                        <ButtonGroup
                            value={serviceFilterValue}
                            onChange={(value) => {
                                setServiceFilterValue(value);
                            }}
                            type="outline"
                            size="small"
                        >
                            <ButtonOption value="all">All</ButtonOption>
                            <ButtonOption value="prime">Prime</ButtonOption>
                            <ButtonOption value="free">AMF</ButtonOption>
                        </ButtonGroup>
                        <Button type="link" size="small" onClick={exportToCSV}>
                            <Icon tokens={downloadLargeTokens} />
                            Download
                        </Button>
                    </Row>
                </Row>
            </Box>

            <div className="combined-rates-grid">
                {isLoading ? (
                    <Column spacing="300" alignmentHorizontal="center" alignmentVertical="center" height="400px">
                        <Loader percentage={loadingProgress.percentage} size="large" />
                        <Text>
                            {loadingProgress.step}: {loadingProgress.detail}
                        </Text>
                    </Column>
                ) : (
                    <BaseAgGrid
                        columnDefs={reportingGridColDefs}
                        defaultColDef={defaultColDef}
                        rowData={gridData}
                        filterText={filterText}
                        setAgGridApi={setAgGridApi}
                        gridOptions={gridOptions}
                        sideBar={true}
                        suppressSizeColumnsToFit={true}
                        onFilterChanged={onFilterChanged}
                    />
                )}
            </div>
        </Column>
    );
};
